import { useCallback, useEffect, useRef, useState } from 'react';
import { emailService } from '../services/api';

// A batch of 60 invitations at 25 per 5 minutes takes ~10 minutes to drain;
// after that the E-mail delivery page is the place to look, not a dialog.
const MAX_POLL_MS = 10 * 60 * 1000;

/**
 * Follow one outbox batch until every row has settled.
 *
 * The dialogs that send (invitations, the urgent reminder) get their answer
 * back before the worker has done anything — the mail server only takes 25 per
 * window — so they poll the batch to turn "queued" into "sent 09:41" or
 * "failed: …" while the admin is still looking. Polling stops on its own once
 * the API reports the batch complete, after MAX_POLL_MS, or on unmount.
 *
 * @param {string|null} batchId - the batchId from the send response; null = idle
 * @param {{intervalMs?: number, enabled?: boolean}} [options]
 * @returns {{batch: object|null, loading: boolean, error: Error|null, refresh: Function}}
 */
export const useEmailBatch = (batchId, { intervalMs = 3000, enabled = true } = {}) => {
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Bumped by refresh() to restart the polling loop (and its 10-minute budget).
  const [generation, setGeneration] = useState(0);
  // The latest batch the dialog asked for; a late response from an earlier
  // batch must not overwrite it.
  const currentBatchRef = useRef(batchId);

  useEffect(() => {
    currentBatchRef.current = batchId;
    setBatch(null);
    setError(null);
  }, [batchId]);

  useEffect(() => {
    if (!batchId || !enabled) return undefined;

    let active = true;
    let timer = null;
    const startedAt = Date.now();

    function schedule() {
      if (!active || Date.now() - startedAt >= MAX_POLL_MS) return;
      timer = setTimeout(poll, intervalMs);
    }

    async function poll() {
      if (!active) return;
      setLoading(true);
      try {
        const data = await emailService.getBatch(batchId);
        if (!active || currentBatchRef.current !== batchId) return;
        setBatch(data);
        setError(null);
        if (!data || !data.complete) schedule();
      } catch (err) {
        if (!active) return;
        setError(err);
        // A batch nobody queued (nothing reached the outbox) will never turn
        // up: stop, rather than ask every 3 s for ten minutes. Anything else
        // is transient and no reason to give up: keep polling within the
        // budget; the dialog keeps showing the last good answer.
        if (err && (err.status === 404 || err.code === 'OUTBOX_BATCH_NOT_FOUND')) return;
        schedule();
      } finally {
        if (active) setLoading(false);
      }
    }

    poll();

    return () => {
      active = false;
      if (timer) clearTimeout(timer);
    };
  }, [batchId, enabled, intervalMs, generation]);

  const refresh = useCallback(() => setGeneration((g) => g + 1), []);

  return { batch, loading, error, refresh };
};

/** The live outbox row for one send-response result, once the batch poll has it. */
export const findBatchItem = (batch, outboxId) => (
  batch && outboxId ? (batch.items || []).find((item) => item.id === outboxId) || null : null
);

/**
 * Where one result of a send stands. The live row wins over the send
 * response; before the first poll the response's `delivery` is all we know.
 * @returns {{status: 'queued'|'sent'|'failed'|'cancelled'|null, sentAt: string|null, lastError: string|null}}
 */
export const deliveryOf = (result, batch) => {
  const live = findBatchItem(batch, result && result.outboxId);
  if (live) return { status: live.status || null, sentAt: live.sentAt || null, lastError: live.lastError || null };
  if (!result) return { status: null, sentAt: null, lastError: null };
  const status = result.delivery || (result.queued ? 'queued' : result.emailed ? 'sent' : null);
  return { status, sentAt: null, lastError: null };
};

/** sent / waiting / failed for a dialog's live line: the batch when polled, else the send response. */
export const deliveryCounts = (results, batch) => {
  if (batch && batch.counts) return batch.counts;
  const list = Array.isArray(results) ? results : [];
  const count = (status) => list.filter((r) => deliveryOf(r, null).status === status).length;
  return {
    total: list.length,
    sent: count('sent'),
    queued: count('queued'),
    failed: count('failed'),
    cancelled: count('cancelled'),
  };
};

export const formatClockTime = (value) => (
  value ? new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''
);

/**
 * Plain-English wording for the admin dialogs (which, unlike the E-mail
 * delivery page, are not localized): 'Queued', 'Sent 09:41', 'Failed: …'.
 * null when the mail was never queued — the caller then falls back to its own
 * "no real e-mail" / copy-the-link cases.
 * @returns {{status: string, tone: 'ok'|'warn'|'bad', text: string}|null}
 */
export const describeDelivery = (result, batch) => {
  const delivery = deliveryOf(result, batch);
  switch (delivery.status) {
    case 'sent':
      return { status: 'sent', tone: 'ok', text: delivery.sentAt ? `Sent ${formatClockTime(delivery.sentAt)}` : 'Sent' };
    case 'failed':
      return { status: 'failed', tone: 'bad', text: `Failed: ${delivery.lastError || 'the mail server rejected it'}` };
    case 'cancelled':
      return { status: 'cancelled', tone: 'bad', text: 'Cancelled' };
    case 'queued':
      return { status: 'queued', tone: 'warn', text: 'Queued' };
    default:
      return null;
  }
};

export default useEmailBatch;
